import React, { useCallback, useMemo } from 'react';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useModal } from 'modules/taboola-common-frontend-modules/modals';
import { BYPASS_PBP_RESTRICTIONS_PERMISSION } from './consts';
import styles from './preventPBPAudienceMixingModal.module.scss';

const getModalConfig = audienceName => ({
    title: (
        <FormattedMessage
            id="campaign.editor.targeting.my.audiences.preventPBPMixing.modal.title"
            defaultMessage="Non-Predictive Audiences will be removed"
            tagName="h2"
        />
    ),
    content: (
        <FormattedMessage
            id="campaign.editor.targeting.my.audiences.preventPBPMixing.modal.content"
            defaultMessage={`Adding "{audienceName}" will remove all non-Predictive "My Audiences." Continue?`}
            values={{ audienceName }}
        />
    ),
    className: styles['modal-container'],
    contentClassName: styles['content'],
    formProps: {
        submitButtonText: (
            <FormattedMessage
                id="campaign.editor.targeting.my.audiences.preventPBPMixing.modal.submitButtonText"
                defaultMessage="Yes, Remove"
            />
        ),
    },
});

export const usePreventPBPAudiencesMixing = ({ clearNonPBPIncludedItems, isTargetingTypeInclude }) => {
    const { open } = useModal();
    const isPreventPBPAudienceMixingEnabled = useConfigMatch({
        [FEATURE_FLAGS.ADS_CONSOLE_PREVENT_PBP_NON_PBP_AUDIENCE_TYPE_MIXING_IN_EDIT_CAMPAIGN]: 'true',
    });
    const isPbpRestrictionsBypassPermitted = usePermissions([BYPASS_PBP_RESTRICTIONS_PERMISSION]);

    const clearNonPBPAudiences = useCallback(async () => {
        clearNonPBPIncludedItems();
    }, [clearNonPBPIncludedItems]);

    const openPreventPBPAudienceMixingModal = useCallback(
        async audience => {
            const { name: audienceName } = audience;
            const result = await open(getModalConfig(audienceName));
            if (result) {
                clearNonPBPAudiences();
                return true;
            }
            return false;
        },
        [clearNonPBPAudiences, open]
    );

    const shouldPreventPBPAudienceMixing = useMemo(
        () => isPreventPBPAudienceMixingEnabled && isTargetingTypeInclude && !isPbpRestrictionsBypassPermitted,
        [isPbpRestrictionsBypassPermitted, isPreventPBPAudienceMixingEnabled, isTargetingTypeInclude]
    );

    return { openPreventPBPAudienceMixingModal, shouldPreventPBPAudienceMixing };
};
